import { useState, useRef } from "react";

import { Menu } from "primereact/menu";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { CaseStatus, UserRole } from "../../constants";
import { Button } from "primereact/button";
import { useClients, useMedicalCenters } from "../../hooks/queries";
import { Dropdown } from "primereact/dropdown";
import { truncateText } from "../../utils";
import { getExportCases } from "../../services/medicalCases";
import { useInfiniteQuery } from "@tanstack/react-query";

const AVAILABLE_FIELDS = [
  { label: "Caso #", field: "id" },
  { label: "Creador del Caso", field: "creator" },
  { label: "Siniestro", field: "service_id" },
  { label: "Cliente", field: "company_client" },
  { label: "Filial", field: "subsidiary_name" },
  { label: "Fecha Creación", field: "created" },
  { label: "CI Titular", field: "holder_ci" },
  { label: "CI Paciente", field: "patient_ci" },
  { label: "Nombre Paciente", field: "patient_name" },
  { label: "Centro médico", field: "medical_center_name" },
  { label: "Estado", field: "state_name" },
  { label: "Dirección", field: "address" },
  { label: "Teléfono Paciente", field: "patient_phone" },
  {
    label: "Observación",
    field: "observation_rms",
    permission: UserRole.MASTER,
  },
  { label: "Status", field: "status" },
  { label: "Tipo de requerimiento", field: "type_requirement_name" },
  { label: "Requerimiento", field: "requirements_name" },
  { label: "Diagnóstico", field: "diagnosis_name" },
  { label: "Monto", field: "amount" },
];

const ExportCasesDialog = ({ visible, onHide, initialStatus = [] }) => {
  const role = localStorage.getItem("role");
  const isAdmin = localStorage.getItem("isAdmin") === "true";
  const isAdminOrMaster = isAdmin || role === UserRole.MASTER;

  const [date, setDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);
  const [selectedMedicalCenters, setSelectedMedicalCenters] = useState(null);
  const [selectDownloadType, setSelectDownloadType] = useState(null);

  const [exportFields, setExportFields] = useState(null);

  const [download, setDownload] = useState(false);

  const [lazyExportFilters, setLazyExportFilters] = useState({
    status: null,
    subsidiary__company_client: null,
    created_at__date__range: null,
    service_id__isnull: null,
    medical_center: null,
  });

  const medicalCenters = useMedicalCenters({
    clientID: lazyExportFilters.subsidiary__company_client,
    enabled: isAdminOrMaster && !!lazyExportFilters.subsidiary__company_client,
  });

  const menuRight = useRef(null);

  const items = [
    {
      label: "Descargar PDF",
      icon: "pi pi-file-pdf",
      command: () => {
        setSelectDownloadType("pdf");
        setDownload(true);
      },
    },
    {
      label: "Descargar Excel",
      icon: "pi pi-file-excel",
      command: () => {
        setSelectDownloadType("excel");
        setDownload(true);
      },
    },
  ];

  const {
    data,
    fetchNextPage,
    hasNextPage,
    // isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery({
    queryKey: ["cases-all", lazyExportFilters],
    queryFn: (params) =>
      getExportCases({ filters: lazyExportFilters, ...params }),
    getNextPageParam: (lastPage, pages) => lastPage.nextPage,
    enabled:
      lazyExportFilters?.status__in !== null &&
      lazyExportFilters?.created_at__date__range !== null &&
      download,
  });

  const clientsQuery = useClients(false);

  const cols = exportFields?.map((f) => ({ ...f, header: f.label }));

  const exportColumns = cols
    ?.filter(
      (col) => role === UserRole.MASTER || col.permission !== UserRole.MASTER
    )
    ?.map((col) => ({
      title: col.header,
      dataKey: col.field,
    }));

  if (hasNextPage && !isFetching) {
    fetchNextPage();
  }

  const exportExcel = (data) => {
    const results = data?.pages
      .map((page) => page.results)
      .reduce((acc, curr) => acc.concat(curr), []);

    const dataMap = results.map((item) => {
      const fields = Object.fromEntries(
        exportFields?.map((f) => [f.label, item[f.field]])
      );
      if (role === UserRole.MASTER) {
        const observation = truncateText(item.observation_rms, 120);
        return {
          ...fields,
          Observación: observation,
        };
      }

      return fields;
    });
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(dataMap);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      saveAsExcelFile(excelBuffer, "Casos-Médicos-Citamed");
    });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    import("file-saver").then((module) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(
          data,
          fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
        );
      }
    });
  };
  const exportPdf = (data) => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default("l", 0, "a3");
        const img = new Image();

        img.src = "/citamed_logo.jpg";
        doc.addImage(img, "JPG", 10, 5, 50, 15);
        doc.setFontSize(14);
        lazyExportFilters?.status__in &&
          doc.text(
            `Reporte de casos: ${lazyExportFilters.status__in
              .split(",")
              .map((status) => CaseStatus.labels[status])
              .join(", ")
              .replace(/, ([^,]*)$/, " y $1")}`,
            80,
            20
          );

        const date_report =
          lazyExportFilters.created_at__date__range.split(",");
        // .map((date) => date.toISOString().split("T")[0]);

        doc.text(`Del ${date_report.join(" al ")}`, 100, 30);

        const results = data?.pages
          .map((page) => page.results)
          .reduce((acc, curr) => acc.concat(curr), [])
          .map((item, index) => {
            return {
              ...item,
              observation_rms: item?.observation_rms
                ? truncateText(item?.observation_rms, 100)
                : "",
            };
          });

        doc.autoTable(exportColumns, results ?? [], {
          startY: 50,
        });
        // const total = casesAllQuery.data?.results.reduce(
        //   (acc, item) => acc + item.amount_usd,
        //   0
        // );
        // const totalFormat = currencyFormat(total);

        // let first = doc.autoTable.previous;
        // doc.autoTable(
        //   [
        //     { title: " ", dataKey: "label" },
        //     { title: " ", dataKey: "sum" },
        //   ],
        //   [{ label: "Total", sum: `$ ${totalFormat}` }],
        //   {
        //     startY: first.finalY + 15,
        //   }
        // );
        doc.save("casos-medicos-citamed.pdf");
      });
    });
  };

  if (data?.pages && data.pages.length > 0 && download) {
    const lastPage = data.pages[data.pages.length - 1];
    if (!lastPage.nextPage) {
      if (selectDownloadType === "excel") {
        exportExcel(data);
      } else if (selectDownloadType === "pdf") {
        exportPdf(data);
      }
      setDownload(false);
    }
  }

  if (
    clientsQuery.data &&
    clientsQuery.data.length > 0 &&
    !lazyExportFilters.subsidiary__company_client
  ) {
    setLazyExportFilters({
      ...lazyExportFilters,
      subsidiary__company_client: clientsQuery.data[0].id,
    });
  }

  return (
    <Dialog
      header="Exportar Casos"
      visible={visible}
      style={{ width: "50vw" }}
      onHide={onHide}
    >
      {role === UserRole.MASTER && (
        <div className="p-fluid mb-2">
          <label htmlFor="service">Con Siniestro</label>
          <Dropdown
            value={lazyExportFilters.service_id__isnull}
            options={[
              { label: "Sí", value: true },
              { label: "No", value: false },
            ]}
            onChange={(e) => {
              setLazyExportFilters({
                ...lazyExportFilters,
                service_id__isnull: e.value,
              });
            }}
            placeholder="Seleccionar opción"
            className="p-column-filter min-w-min"
          />
        </div>
      )}
      <div className="p-fluid mb-2">
        <label htmlFor="client">Seleccione Cliente</label>
        <Dropdown
          value={lazyExportFilters.subsidiary__company_client}
          options={clientsQuery.data ?? []}
          optionLabel="name"
          optionValue="id"
          onChange={(e) => {
            setLazyExportFilters({
              ...lazyExportFilters,
              subsidiary__company_client: e.value,
            });
          }}
          placeholder="Seleccionar cliente"
          className="p-column-filter min-w-min"
          showClear
        />
      </div>

      {isAdminOrMaster && (
        <div className="p-fluid mb-2">
          <label htmlFor="medicalCenter">Seleccione Proveedor</label>
          <MultiSelect
            options={medicalCenters.data || []}
            loading={medicalCenters.isLoading}
            className="w-full md:full"
            optionValue="id"
            optionLabel="name"
            value={selectedMedicalCenters}
            onChange={(e) => {
              setLazyExportFilters({
                ...lazyExportFilters,
                medical_center__in: e.value.join(","),
              });
              setSelectedMedicalCenters(e.value);
            }}
            display="chip"
            placeholder="Seleccione Proveedor"
            maxSelectedLabels={3}
            filter
            filterBy="name"
            filterPlaceholder="Buscar proveedor"
            showClear
            emptyFilterMessage="No se encontraron proveedores"
            emptyMessage="No hay proveedores disponibles"
          />
        </div>
      )}

      <div className="p-fluid">
        <label htmlFor="client">Seleccione Status</label>
        <MultiSelect
          options={Object.entries(CaseStatus.labels)
            .filter(([value, key]) => value !== CaseStatus.PRIORITIZED)
            .map(([key, value]) => ({
              label: value,
              value: key,
            }))}
          className="w-full md:full"
          optionValue="value"
          optionLabel="label"
          value={selectedStatus}
          onChange={(e) => {
            if (e.value != null) {
              setLazyExportFilters({
                ...lazyExportFilters,
                status__in: e.value.join(","),
              });
            }

            setSelectedStatus(e.value);
          }}
          display="chip"
          placeholder="Seleccione Status"
          maxSelectedLabels={3}
        />
        <div className="field mt-2">
          <label htmlFor="date">Rango de fechas</label>
          <Calendar
            id="date"
            name="date"
            locale="es"
            label="Rango de fechas"
            className="flex w-full"
            value={date}
            onChange={(e) => {
              setDate(e.value);

              if (
                e.value != null &&
                e.value.length === 2 &&
                e.value[0] &&
                e.value[1]
              ) {
                const range = e.value
                  .map((date) => date.toISOString().split("T")[0])
                  .join(",");
                setLazyExportFilters({
                  ...lazyExportFilters,
                  created_at__date__range: range,
                });
              }
            }}
            selectionMode="range"
            hideOnRangeSelection
            dateFormat="dd/mm/yy"
            readOnlyInput
            showButtonBar
            style={{ width: "250px" }}
          />
        </div>
        <div className="field mt-2">
          <label htmlFor="fields">Seleccione los campos a exportar</label>
          <MultiSelect
            options={AVAILABLE_FIELDS}
            className="w-full md:full"
            useOptionAsValue
            optionLabel="label"
            value={exportFields}
            onChange={(e) => setExportFields(e.value)}
            display="chip"
            placeholder="Seleccionar campos"
          />
        </div>

        <div className="mt-3 flex gap-3">
          <Button
            label="Cerrar"
            icon="pi pi-times"
            severity="secondary"
            onClick={onHide}
          />

          <Menu model={items} popup ref={menuRight} id="popup_menu_right" />

          <Button
            label="Descargar"
            severity="warning"
            className="p-button-success"
            onClick={(event) => menuRight.current.toggle(event)}
            loading={isFetching}
            disabled={
              lazyExportFilters?.status__in === null ||
              lazyExportFilters?.created_at__date__range === null ||
              exportFields === null
            }
          />
        </div>
      </div>
    </Dialog>
  );
};

export default ExportCasesDialog;
